// libraries
import React, { useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { debounce } from 'lodash'
import { useForm, FormProvider, Controller } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Typography,
  Stack,
  InputAdornment,
  IconButton,
  Autocomplete,
  TextField,
  Tooltip,
  Alert,
} from '@mui/material'
import {
  AttachMoney as AttachMoneyIcon,
  Save as SaveIcon,
  Print as PrintIcon,
  LocalShipping as LocalShippingIcon,
  ManageSearch as ManageSearchIcon,
  ArrowDropDown as ArrowDropDownIcon,
  MoreVert as MoreVertIcon,
  Lock as LockIcon,
} from '@mui/icons-material'
import { Incomplete as IncompleteIcon, Paid as PaidIcon } from 'components/assets'
import { useGridApiRef } from '@mui/x-data-grid-pro'
import { randomId } from '@mui/x-data-grid-generator'
import { useTheme } from '@mui/material/styles'
import { useSelector, useDispatch } from 'react-redux'
import printJS from 'print-js'

// state
import store from 'state/store'
import { showFormModal } from 'state/ui'
import {
  clearConsumerInformation,
  setConsumerInfo,
  setOutboundTicketId,
  clearSubmittedFromDispatchBlade,
} from 'state/outboundTicket'
import { clearSalesOrderFilters, setSalesOrderId, clearSalesOrderId } from 'state/salesOrder'

// queries
import {
  useCreateOutboundTicketMutation,
  useGetOutboundTicketQuery,
  useUpdateOutboundTicketMutation,
  useVoidOutboundTicketMutation,
  useGetPullApartScrapRowsQuery,
  getMaterialRowWithYardFields,
  getLooseMaterials,
  getFinishedGoodMaterials,
  transformOutboundTicket,
} from 'services/outboundTickets'
import { useGetCustomerQuery } from 'services/customers'
import { useGetPackingListsQuery } from 'services/packingList'
import { useGetSalesOrderQuery } from 'services/salesOrders'
import { useGetOrganizationQuery } from 'services/organizations'
import { useGetMaterialTypesQuery } from 'services/materialTypes'

// specific components
import ActiveMaterialList from 'components/containers/outbound-tickets/components/ActiveMaterialList'
import OutboundTicketFooter from 'components/containers/outbound-tickets/components/OutboundTicketFooter'
import FreightCostBlade from 'components/containers/freight/freight-cost/FreightCostBlade'
import PullApartFormComponent from 'components/containers/outbound-tickets/components/PullApartFormComponent'
import DispatchTicketFormAreaObt from 'components/containers/outbound-tickets/components/link-dispatch/DispatchTicketFormAreaObt'

// generic components
import PrintDialog from 'components/generic/printable/PrintDialog'
import PrintablePackingListOrBoL from 'components/generic/printable/PrintablePackingListOrBoL'
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import TabsLayoutContext from 'components/layouts/TabsLayoutContextProvider'
import AlertSnackbar from 'components/generic/snackbars'
import Select from 'components/generic/form/Select'
import { Chip, SelectChip } from 'components/generic/chip'
import InvoicedStatusChip from 'components/generic/status/InvoicedStatusChip'
import { ACTION_TYPE } from 'components/generic/buttons/ButtonList'
import Input from 'components/generic/form/Input'
import Checkbox from 'components/generic/form/Checkbox'
import DatePickerV2 from 'components/generic/form/DatePickerV2'
import NotesModal, { formatNotes } from 'components/generic/modals/dialog-modal/NotesModal'
import CustomerAutocomplete from 'components/generic/form/AsyncAutocomplete/CustomerAutocomplete'
import AlertModal from 'components/generic/modals/dialog-modal/AlertModal'
import RouterPrompt from 'components/generic/modals/router-prompt/RouterPrompt'
import MaterialTypesDataFetcher from 'components/generic/MaterialTypesDataFetcher'
import EmailModal from 'components/generic/modals/dialog-modal/EmailModal'

// helpers and utils
import FORM_MODALS from 'utils/ui/modals'
import { request } from 'customRequest'
import { trackEvent } from 'utils/tracking'
import { formOptions } from 'utils/validation/outboundTicket'
import {
  OUTBOUND_TICKET_STATUSES,
  CUSTOMERS_FILTERS,
  CHURN_ZERO_EVENTS as cz,
  COMMON_STATUSES,
  TRANSPORTATION_TYPE_OPTIONS,
  BILL_STATUSES,
  DEFAULT_AXLE_DATA,
  ENTITY_IN_LOCK_PERIOD_MESSAGE,
  DEFAULT_PDF_WIDTH,
  PDF_TYPE,
  GS_ERROR_CODE,
} from 'utils/constants'
import {
  locationIsNotEmpty,
  dispatchSnackbar,
  getCountries,
  getCountryFromState,
  checkIfDateIsLocked,
  generateLockTooltip,
} from 'utils/helpers'
import {
  statusArray,
  initialOutboundTicketInformation,
  initialLoadInformation,
  calculateTotalInfoFields,
  formatPullApartScrapRows,
} from 'components/containers/outbound-tickets/utils'
import { OUTBOUND_TICKET_IMAGE_TYPE } from 'components/containers/outbound-tickets/helpers/images'
import { useCalculateFreightCost } from 'utils/custom-hooks/useCalculateFreightCost'
import useGetPricePrepend from 'utils/custom-hooks/useGetPricePrepend'
import { dateFormatter, timeFormatter, makeCommaSeparatedString } from 'utils/data-manipulation/valueFormatter'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import { addToDate } from 'utils/data-manipulation/common'
import useQzTray from 'utils/custom-hooks/useQzTray'
import { usePatchDispatchTicketMutation } from 'services/dispatch'
import StateProvinceInput from '../customers/locations/StateProvinceInput'

const OutboundTicketContainer = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const theme = useTheme()
  const {
    useMinimalObt,
    showNotesFromSOOnBOL,
    supportVoidInvoice,
    enablePdfEmails,
    enableFePdfs,
    enablePullApartFeatures,
    enableSalesOrderFreightCosts,
    responsiveBOL,
    emailBOLsAndPLs,
    enableCountryCodes,
    enableLockAccountingV2,
  } = useSelector(state => state.featureFlags?.variables || {})
  const { isClearingReduxFormState } = useSelector(state => state.ui || {})
  const { defaultYardUm, settings } = useSelector(state => state.settings)
  const { country: yardCountry } = useSelector(state => state.settings.details)
  const { defaultNumberOfAxles, enableAxleWeights } = settings?.purchaseSettings?.inboundTickets || {}
  const { lockAccountingPeriod } = settings?.overviewSettings?.overview || {}
  const context = useContext(ContentLayoutContext)
  const tabsContext = useContext(TabsLayoutContext)
  const countries = useMemo(() => getCountries(), [])
  const [showTransfer, setShowTransfer] = useState(false)
  const [showUnlinkModal, setShowUnlinkModal] = useState(false)
  const [isUnlinking, setIsUnlinking] = useState(false)

  const { id: outboundTicketId } = useParams()
  const isUpdate = !!outboundTicketId

  const materialsApiRef = useGridApiRef()
  const totalApiRef = useGridApiRef()
  const methods = useForm(formOptions)
  const { handleSubmit, control, formState, reset, getValues, setValue, setFocus, watch } = methods
  const formValues = getValues()
  const { errors, dirtyFields, isValid } = formState
  const isDirty = !!Object.keys(dirtyFields).length
  const [errorMessage, setErrorMessage] = useState('')
  const [openNotes, setOpenNotes] = useState(false)
  const [loadNotes, setLoadNotes] = useState([])
  const [loadInformation, setLoadInformation] = useState(initialLoadInformation)
  const [initialMaterialRows, setInitialMaterialRows] = useState([])
  const [initialTotalInformation, setInitialTotalInformation] = useState([])
  const [showVoidModal, setShowVoidModal] = useState(false)
  const [activeDialog, setActiveDialog] = useState(null)
  const { consumerInfo: consumerInfoState, submittedFromDispatchBlade } = useSelector(state => state.outboundTicket)
  const salesOrderId = consumerInfoState?.salesOrderId
  const [salesOrderEntriesObject, setSalesOrderEntriesObject] = useState([])
  const hasSalesOrderLinked = () => !!consumerInfoState?.salesOrder
  const { token, defaultYardTimezone } = useSelector(state => state.auth)
  const [hasEditedMaterials, setHasEditedMaterials] = useState(false)
  const [customerId, setCustomerId] = useState(null)
  const salesOrderShouldDirty = useRef(false)
  const [isTransfer, setIsTransfer] = useState(false)
  const [customersFilter, setCustomersFilter] = useState(CUSTOMERS_FILTERS.CONSUMER)
  const [customerLocations, setCustomerLocations] = useState([])
  const [hasReset, setHasReset] = useState(false)
  const [freightDestinationCustomerName, setFreightDestinationCustomerName] = useState('')
  const [openFreightCostBlade, setOpenFreightCostBlade] = useState(false)
  const [cachedMaterialTypesData, setCachedMaterialTypesData] = useState({})
  const initialRender = useRef(true)
  const selectedCarrier = watch('transportationInfo.carrierLookup')
  const transportationType = watch('transportationInfo.transportationType')
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false)
  const [isFetchingPdf, setIsFetchingPdf] = useState(false)
  const { qzPrint, qzPrintResponsive } = useQzTray()
  const [pullApartRemovedRowsMessage, setPullApartRemovedRowsMessage] = useState('')

  const { appRouteLoadBoard, enableSoExport } = useSelector(state => state.featureFlags?.variables || {})

  const hasInternationalCustomers = customerLocations.some(location => !!location.country && location.country !== 'US')

  const materialTypeIds = useMemo(() => {
    const set = new Set()
    initialMaterialRows.forEach(material => {
      set.add(material?.code?.materialTypeId || '')
    })
    return Array.from(set)
  }, [initialMaterialRows])

  // We can remove the following query once we implement pagination for packing lists
  const { isLoading: isLoadingPackingLists } = useGetPackingListsQuery({
    filter: JSON.stringify({
      statuses: [COMMON_STATUSES.AVAILABLE.id],
    }),
  })

  const { data: outboundTicketData, isFetching: isFetchingOutboundTicket } = useGetOutboundTicketQuery(
    { outboundTicketId },
    {
      refetchOnMountOrArgChange: true,
      skip: !isUpdate,
    },
  )

  const { data: customerData, isLoading: isLoadingCustomerData } = useGetCustomerQuery(
    { customerId },
    {
      refetchOnMountOrArgChange: true,
      skip: !customerId,
    },
  )

  const { data: carrierData, isLoading: isLoadingCarrierData } = useGetCustomerQuery(
    { customerId: selectedCarrier?.id },
    {
      refetchOnMountOrArgChange: true,
      skip: !selectedCarrier?.id,
    },
  )

  const { data: organizationData, isLoading: isLoadingOrganizationData } = useGetOrganizationQuery({}, { skip: !token })

  const { data: salesOrderData, isLoading: isLoadingSalesOrderData } = useGetSalesOrderQuery(
    { salesOrderId },
    {
      refetchOnMountOrArgChange: true,
      skip: !salesOrderId,
    },
  )

  const {
    data: pullApartScrapRowsData,
    error: pullApartScrapRowsError,
    isFetching: isFetchingPullApartScrapRows,
  } = useGetPullApartScrapRowsQuery({}, { refetchOnMountOrArgChange: true, skip: !enablePullApartFeatures })

  // prefetch initial material dropdown results (GREEN-8185)
  useGetMaterialTypesQuery({
    filter: JSON.stringify({ search: '', priceSheetId: salesOrderData?.priceSheet?.id || '' }),
    pageSize: 50,
  })

  const [createOutboundTicket, { isLoading: isLoadingCreatingTicket }] = useCreateOutboundTicketMutation()

  const [updateOutboundTicket, { isLoading: isLoadingUpdatingTicket }] = useUpdateOutboundTicketMutation()

  const [voidOutboundTicket, { isLoading: isLoadingVoidTicket }] = useVoidOutboundTicketMutation()

  const [patchDispatchTicket] = usePatchDispatchTicketMutation()

  const isLoading =
    isLoadingPackingLists ||
    isFetchingOutboundTicket ||
    isLoadingCreatingTicket ||
    isLoadingUpdatingTicket ||
    isLoadingVoidTicket ||
    isLoadingSalesOrderData ||
    isLoadingCustomerData ||
    isLoadingCarrierData ||
    isLoadingOrganizationData ||
    isFetchingPdf ||
    isFetchingPullApartScrapRows

  useGlobalLoader(isLoading)

  const { pricePrepend: carrierPricePrepend } = useGetPricePrepend({
    customerData: carrierData,
    selectedCustomer: selectedCarrier,
  })

  const freightBillIsPaid = useMemo(() => {
    const freightBillStatus = outboundTicketData?.freightCost?.freightBill?.status
    return [BILL_STATUSES.PAID.id, BILL_STATUSES.PARTIAL.id].indexOf(freightBillStatus) > -1
  }, [outboundTicketData])

  const isInLockedAccountingPeriodV1 = useMemo(() => {
    if (enableLockAccountingV2) return false // if lock accounting V2 is turned on, we do not need to disable any fields
    if (!outboundTicketData || !lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return false
    return (
      new Date(outboundTicketData.effectiveDate).getTime() < new Date(lockAccountingPeriod.lockAccountingDate).getTime()
    )
  }, [lockAccountingPeriod, outboundTicketData, enableLockAccountingV2])

  const isInLockedAccountingPeriodV2 = useMemo(() => {
    if (!enableLockAccountingV2) return false
    if (!outboundTicketData || !lockAccountingPeriod || !lockAccountingPeriod.isLockedForAccounting) return false
    return checkIfDateIsLocked(lockAccountingPeriod, outboundTicketData.effectiveDate)
  }, [lockAccountingPeriod, outboundTicketData, enableLockAccountingV2])

  const canVoidTicket = useMemo(() => {
    if (isInLockedAccountingPeriodV1) return false
    if (!outboundTicketData?.status) return false // status is not set yet
    const status = outboundTicketData?.status

    if (
      [
        OUTBOUND_TICKET_STATUSES.VOID.id,
        OUTBOUND_TICKET_STATUSES.INVOICED.id,
        OUTBOUND_TICKET_STATUSES.PAID.id,
      ].includes(status)
    )
      return false
    return true
  }, [isInLockedAccountingPeriodV1, outboundTicketData])

  const setShippingInfo = (newLocation, shouldDirty = true) => {
    const defaultCountry = enableCountryCodes && !isUpdate && !hasSalesOrderLinked() ? yardCountry : undefined
    if (newLocation) {
      setValue('shippingInfo.locationId', newLocation.id || newLocation.locationId || null, { shouldDirty })
      setValue('shippingInfo.city', newLocation.city || '', { shouldDirty })
      setValue('shippingInfo.state', newLocation.state || '', { shouldDirty })
      setValue('shippingInfo.zip', newLocation.zip || '', { shouldDirty })
      setValue('shippingInfo.country', newLocation.country || defaultCountry, { shouldDirty })
      setValue('shippingInfo.name', newLocation?.name || newLocation?.locationName || '', { shouldDirty })
      setValue(
        'shippingInfo.shippingAddress',
        newLocation?.shippingAddress || newLocation?.address1 || newLocation?.address || '',
        { shouldDirty },
      )
    }
  }
  const getShippingInfo = () => ({
    locationId: formValues?.shippingInfo?.locationId,
    city: formValues?.shippingInfo?.city,
    state: formValues?.shippingInfo?.state,
    zip: formValues?.shippingInfo?.zip,
    country: formValues?.shippingInfo?.country,
    name: formValues?.shippingInfo?.name,
    address: formValues?.shippingInfo?.shippingAddress,
  })

  useEffect(() => {
    if (salesOrderData) {
      dispatch(setSalesOrderId(salesOrderData?.id))
      setSalesOrderEntriesObject(salesOrderData?.priceSheet?.entriesObj)

      const updatedConsumerInfo = {
        ...getValues('consumerInfo'),
        millDeliveryNumber: salesOrderData.millDeliveryNumber,
      }
      setValue('consumerInfo', updatedConsumerInfo, {
        shouldDirty: salesOrderShouldDirty.current,
      })

      if (salesOrderData?.freightDestination && hasSalesOrderLinked()) {
        setFreightDestinationCustomerName(salesOrderData?.freightDestination?.customerName)
        setShippingInfo(salesOrderData.freightDestination, false)
      }
      if (enableSoExport) {
        const isExportValue = salesOrderShouldDirty.current
          ? salesOrderData?.isExport
          : formValues.transportationInfo?.export
        setValue('transportationInfo.export', isExportValue || false, { shouldDirty: salesOrderShouldDirty.current })
      }
    } else if (!salesOrderId) {
      setFreightDestinationCustomerName('')
      dispatch(clearSalesOrderId())
      setShippingInfo({}, false)
    }

    return () => dispatch(clearSalesOrderId())
  }, [consumerInfoState, salesOrderData])

  useEffect(() => {
    const defaultShippingLocation = customerLocations?.find(location => !!location.isDefaultShippingLocation)
    const location = defaultShippingLocation || customerLocations[0]

    if (isDirty && !consumerInfoState?.salesOrder) {
      setShippingInfo(location || {})
    }
  }, [customerLocations])

  useEffect(() => {
    if (customerData) {
      if (customerData.isYard) {
        setIsTransfer(true)
        setCustomersFilter(CUSTOMERS_FILTERS.YARD)
      } else {
        setIsTransfer(false)
        setCustomersFilter(CUSTOMERS_FILTERS.CONSUMER)
      }

      const validCustomerLocations = customerData?.locations?.filter(location => locationIsNotEmpty(location)) || []
      setCustomerLocations(
        validCustomerLocations.map(location => ({
          id: location?.id,
          city: location?.city,
          state: location?.state,
          zip: location?.zip,
          country: location?.country,
          name: location?.name || location?.locationName,
          shippingAddress: location?.address1 || location?.address,
          freightPricing: location?.freightPricing,
          isDefaultShippingLocation: location?.isDefaultShippingLocation,
        })),
      )
      if (!hasReset && !hasSalesOrderLinked()) {
        setShippingInfo(validCustomerLocations[0] || {})
      }
    }
  }, [customerData, outboundTicketData])

  useEffect(() => {
    if (organizationData?.yards?.length > 1) {
      setShowTransfer(true)
    }
  }, [organizationData])

  const handleClickTransfer = event => {
    setValue('consumerInfo.consumerName', '')
    dispatch(clearConsumerInformation())
    dispatch(clearSalesOrderFilters())
    dispatch(clearSalesOrderId())
    setShippingInfo({})
    setCustomerId(null)
    setIsTransfer(event.target.checked)
    if (event.target.checked) {
      setCustomersFilter(CUSTOMERS_FILTERS.YARD)
    } else {
      setCustomersFilter(CUSTOMERS_FILTERS.CONSUMER)
    }
  }

  const handleOpenPrintDialog = option => {
    if (enableFePdfs) {
      if (!outboundTicketData?.id) {
        return
      }

      let pdfTitle = ''
      let pdfUrl = ''

      const obtId = outboundTicketData?.id
      if (option.id == 'packingList') {
        pdfTitle = `Packing List #${outboundTicketData?.tag}`
        pdfUrl = `/core-api/sales/outbound-tickets/${obtId}/pdf/OBT_PACKING_LIST`
      } else if (option.id == 'billOfLading') {
        pdfTitle = `Bill of Lading #${outboundTicketData?.tag}`
        if (responsiveBOL) {
          const paperWidth = localStorage.getItem('paperSize') ?? DEFAULT_PDF_WIDTH
          pdfUrl = `/core-api/sales/outbound-tickets/${obtId}/pdf/OBT_BILL_OF_LADING?width=${paperWidth}`
        } else {
          pdfUrl = `/core-api/sales/outbound-tickets/${obtId}/pdf/OBT_BILL_OF_LADING`
        }
      }

      setIsFetchingPdf(true)

      request(pdfUrl, 'get', {}, { accept: 'application/pdf' }, true)
        .then(response => {
          printJS({
            printable: response,
            type: 'pdf',
            base64: true,
            documentTitle: pdfTitle,
          })
          setIsFetchingPdf(false)
        })
        .catch(() => {
          setIsFetchingPdf(false)
          dispatchSnackbar(dispatch, 'error', 'An error occurred while generating the PDF document')
        })
    } else {
      setActiveDialog(option)
    }
  }

  const handleQzPrint = option => {
    if (!outboundTicketData?.id) {
      return
    }

    let pdfUrl = ''

    const obtId = outboundTicketData?.id
    if (option.id == 'packingList') {
      pdfUrl = `/core-api/sales/outbound-tickets/${obtId}/pdf/OBT_PACKING_LIST`
    } else if (option.id == 'billOfLading') {
      if (responsiveBOL) {
        const paperWidth = localStorage.getItem('paperSize') ?? DEFAULT_PDF_WIDTH
        pdfUrl = `/core-api/sales/outbound-tickets/${obtId}/pdf/OBT_BILL_OF_LADING?width=${paperWidth}`
      } else {
        pdfUrl = `/core-api/sales/outbound-tickets/${obtId}/pdf/OBT_BILL_OF_LADING`
      }
    }

    setIsFetchingPdf(true)

    request(pdfUrl, 'get', {}, { accept: 'application/pdf' }, true)
      .then(response => {
        if (responsiveBOL && option.id === 'billOfLading') {
          qzPrintResponsive(response, localStorage.getItem('ticketPrinter') || '').catch(() => {
            handleOpenPrintDialog(option)
          })
        } else {
          qzPrint(response, localStorage.getItem('documentPrinter') || '').catch(() => {
            handleOpenPrintDialog(option)
          })
        }
        setIsFetchingPdf(false)
      })
      .catch(() => {
        setIsFetchingPdf(false)
        dispatchSnackbar(dispatch, 'error', 'An error occurred while generating the PDF document')
      })
  }

  const handlePrint = selectedOption => {
    const defaultOption = useMinimalObt
      ? getDialogContents(outboundTicketData?.tag)?.find(option => option.id === 'billOfLading')
      : getDialogContents(outboundTicketData?.tag)?.[0]

    const option = selectedOption || defaultOption

    if (
      (responsiveBOL &&
        option.id === 'billOfLading' &&
        (localStorage.getItem('ticketPrinter') === 'none' || !localStorage.getItem('ticketPrinter'))) ||
      (option.id === 'packingList' &&
        (localStorage.getItem('documentPrinter') === 'none' || !localStorage.getItem('documentPrinter'))) ||
      (!responsiveBOL &&
        (localStorage.getItem('documentPrinter') === 'none' || !localStorage.getItem('documentPrinter')))
    ) {
      handleOpenPrintDialog(option)
    } else {
      handleQzPrint(option)
    }
  }

  const handleClosePrintDialog = () => setActiveDialog(null)

  const handleCustomerNameSelected = event => {
    const id = event?.id
    setCustomerId(id)
    if (!id) {
      setShippingInfo({})
    }
  }

  const handleShippingInfoSelected = (_, value) => {
    setShippingInfo(value)
  }
  const handleAutoCompleteChange = (_, __, reason) => {
    if (reason === 'clear') {
      setShippingInfo({}, true)
    }
  }

  const handleInputChange = () => {
    setValue('shippingInfo.name', '')
  }

  const getMaterialRows = useCallback(
    () =>
      typeof materialsApiRef.current.getRowModels === 'function'
        ? Array.from(
            materialsApiRef.current.getRowModels(),

            ([_, materialRow]) => getMaterialRowWithYardFields(materialRow, defaultYardUm),
          )
        : [],
    [materialsApiRef, defaultYardUm],
  )

  const getMaterials = useCallback(
    () =>
      getMaterialRows()
        .filter(row => !!row.materialTypeId && !row.fgId)
        .map(row => getLooseMaterials(row)),
    [materialsApiRef],
  )

  const getTruckWeights = useCallback(() => {
    if (!totalApiRef?.current?.getRow) return

    const truckTotalRow = totalApiRef.current.getRow('truck-total')
    return {
      gross: {
        amount: parseInt(truckTotalRow?.yardGross, 10),
        units: defaultYardUm,
      },
      tare: {
        amount: parseInt(truckTotalRow?.yardTare, 10),
        units: defaultYardUm,
      },
      net: {
        amount: parseInt(truckTotalRow?.yardNet, 10),
        units: defaultYardUm,
      },
      axleWeights: truckTotalRow?.axleWeights || [],
    }
  }, [totalApiRef, defaultYardUm])

  const getPackingLists = useCallback(() => {
    let plIdList = []
    getMaterialRows()
      .filter(row => !!row.fgId && !!row.plId)
      .forEach(row => plIdList.push(`${row.plId}`))

    plIdList = [...new Set(plIdList)]

    const packingList = []
    plIdList.forEach(plId => {
      const finishedGoods = getMaterialRows()
        .filter(row => row.plId === plId)
        .map(row => getFinishedGoodMaterials(row))

      packingList.push({
        id: plId,
        finishedGoods,
      })
    })

    return packingList
  }, [materialsApiRef])

  const getFinishedGoodsList = useCallback(
    () =>
      getMaterialRows()
        .filter(row => !!row.fgId && !row.plId)
        .map(row => getFinishedGoodMaterials(row)),
    [materialsApiRef],
  )

  const getFinishedGoodsListFullData = useCallback(() => getMaterialRows().filter(row => !!row.fgId), [materialsApiRef])

  const calculateTotalInfo = useCallback(() => {
    const materialRows = getMaterialRows().map(material => ({
      ...material,
      yardGross: material.gross,
      yardTare: material.tare,
    }))
    const totalInfoFields = calculateTotalInfoFields(materialRows)

    const { fgCount, loadGross, loadTare, totalNet } = totalInfoFields

    setLoadInformation({
      ...loadInformation,
      loadWeight: {
        fgs: fgCount,
        gross: loadGross,
        tare: loadTare,
        net: totalNet,
      },
    })

    totalApiRef.current.updateRows([
      {
        id: 'load-total',
        code: 'Load Total',
        yardGross: loadGross,
        yardTare: loadTare,
        yardNet: totalNet,
      },
    ])
  }, [loadInformation, materialsApiRef, totalApiRef])

  const getImages = useCallback(() => {
    const materialRows = Array.from(materialsApiRef.current.getRowModels().values())
    const totalRow = totalApiRef.current.getRow('truck-total')
    const images = []

    materialRows.forEach((materialRow, index) => {
      materialRow?.grossImages?.forEach(image => {
        images.push({
          itemId: materialRow.materialTypeId,
          type: OUTBOUND_TICKET_IMAGE_TYPE.MATERIAL_GROSS,
          filename: image.imageFilename,
          isTemp: true,
          rowIndex: index,
          deviceId: image.deviceId,
        })
      })

      materialRow?.tareImages?.forEach(image => {
        images.push({
          itemId: materialRow.materialTypeId,
          type: OUTBOUND_TICKET_IMAGE_TYPE.MATERIAL_TARE,
          filename: image.imageFilename,
          isTemp: true,
          rowIndex: index,
          deviceId: image.deviceId,
        })
      })
    })

    return [
      ...images,
      ...(totalRow?.grossImages?.length > 0
        ? totalRow?.grossImages?.map(image => ({
            type: OUTBOUND_TICKET_IMAGE_TYPE.OUTBOUND_TICKET_GROSS,
            filename: image.imageFilename,
            isTemp: true,
            deviceId: image.deviceId,
          })) || []
        : []),
      ...(totalRow?.tareImages?.length > 0
        ? totalRow?.tareImages?.map(image => ({
            type: OUTBOUND_TICKET_IMAGE_TYPE.OUTBOUND_TICKET_TARE,
            filename: image.imageFilename,
            isTemp: true,
            deviceId: image.deviceId,
          })) || []
        : []),
    ]
  }, [materialsApiRef.current, totalApiRef.current])

  const getDialogContents = ticketTag => [
    {
      id: 'billOfLading',
      title: 'Bill of Lading',
      dialogTitle: `${useMinimalObt ? 'Weight Ticket' : 'Bill of Lading'} #${ticketTag}`,
      action: 'billOfLading',
      width: '100%',
    },
    {
      id: 'packingList',
      title: 'Packing List',
      dialogTitle: `Packing List #${ticketTag}`,
      action: 'packingList',
      width: '100%',
    },
  ]

  const getMoreActionsOptions = () => [
    {
      id: 'void-ticket-action',
      title: 'Void Ticket',
      disabled: !canVoidTicket,
      onClick: () => {
        setShowVoidModal(true)
      },
    },
  ]

  const initConsumerInfo = consumer => {
    const { salesOrder, customerPo, invoiceWeights, consumerName, salesOrderId: consumerSalesOrderId } = consumer
    dispatch(
      setConsumerInfo({
        salesOrder,
        salesOrderId: consumerSalesOrderId,
        customerPo,
        invoiceWeights,
        consumer: consumerName,
      }),
    )
  }

  const addFinishedGoodToMaterialRows = (finishedGood, materialRows, packingList = {}) => {
    materialRows.push({
      id: finishedGood.id,
      orderEntryId: finishedGood.orderEntryId,
      code: {
        materialName: finishedGood?.materialType?.commonName,
        materialCode: finishedGood?.materialType?.code,
        materialTypeId: finishedGood?.materialType?.id,
        orderEntryId: finishedGood?.orderEntryId,
      },
      um: defaultYardUm,
      plId: packingList?.id || '',
      plTag: packingList?.tag || '',
      packListId: packingList?.id || '',
      fgId: finishedGood?.tag || '',
      fgType: finishedGood?.type || '',
      itemCount: finishedGood?.itemCount,
      yardGross: finishedGood.weight?.gross || 0,
      yardTare: finishedGood.weight?.tare || 0,
      yardNet: finishedGood.weight?.net || 0,
    })
  }

  // map is required because of the way the images are stored in the DB
  // 'filename' should be replaced by 'imageFilename' for consistency
  const findOutboundTicketImages = ({ images, imageType, materialTypeId, materialRowIndex }) =>
    images
      ?.filter(
        image => image.type === imageType && image.itemId === materialTypeId && image.rowIndex === materialRowIndex,
      )
      .map(image => ({ ...image, imageFilename: image.filename }))

  /* Code for Pull Apart - START */
  // Check if the user has selected a "FLATS" material (for Pull Apart)
  const materialsContainFlats = getMaterialRows().some(row => {
    return (
      [
        '7d27be17-573b-4388-bf0e-aa6fc9bf5e35', // FLATS - WITH MOTORS (prod)
        '7747df44-88fb-420b-bd0c-e6073f8224a7', // FLATS - WITHOUT MOTORS (prod)
        '0fffc14c-bbb3-4a9e-8248-6dd78140f19e', // FLATS - WITH MOTORS (dev)
        '24404363-eec1-4fe5-875f-5d2a84b7ba21', // FLATS - WITHOUT MOTORS (dev)
      ].indexOf(row.code?.materialTypeId) > -1
    )
  })

  const { hasPullApartError, activePullApartScrapRows } = useMemo(() => {
    if (!enablePullApartFeatures) {
      return {
        hasPullApartError: false,
        activePullApartScrapRows: [],
      }
    }
    return {
      hasPullApartError: !!(!pullApartScrapRowsData || pullApartScrapRowsError),
      activePullApartScrapRows: (pullApartScrapRowsData || []).slice(),
    }
  }, [enablePullApartFeatures, pullApartScrapRowsData, pullApartScrapRowsError])
  /* Code for Pull Apart - END */

  const setupOutboundTicket = async () => {
    dispatch(clearConsumerInformation())
    dispatch(clearSalesOrderFilters())
    dispatch(clearSalesOrderId())
    if (outboundTicketData) {
      const {
        consumerInfo,
        transportationInfo,
        shippingInfo,
        status,
        notes,
        truckWeight,
        docs,
        freightCost,
        axleWeights,
        pullApartRows: savedPullApartScrapRows,
      } = transformOutboundTicket(outboundTicketData)

      /* Code for Pull Apart - START */
      // Remove rows that are still on the OBT but are not active anymore from the Pull Apart GET response.
      // Also retrieve the updated "numberOfVins" from the Pull Apart GET response for each row
      const formattedPullApartScrapRows = formatPullApartScrapRows(savedPullApartScrapRows, activePullApartScrapRows)
      const removedScrapRows = []
      savedPullApartScrapRows.forEach(savedRow => {
        const scrapRowWasRemoved = formattedPullApartScrapRows.every(scrapRow => scrapRow.row !== savedRow.row)
        if (scrapRowWasRemoved) {
          removedScrapRows.push(savedRow.row)
        }
      })
      if (status?.value === OUTBOUND_TICKET_STATUSES.INCOMPLETE?.id) {
        if (removedScrapRows.length > 0) {
          const haveOrHas = removedScrapRows.length > 1 ? 'have' : 'has'
          setPullApartRemovedRowsMessage(
            `Scrap row${removedScrapRows.length > 1 ? 's' : ''} ${makeCommaSeparatedString(removedScrapRows)} ${haveOrHas} already been shipped and ${haveOrHas} been automatically removed from this ticket.`,
          )
        } else {
          setPullApartRemovedRowsMessage('')
        }
      }
      /* Code for Pull Apart - END */

      reset({
        ...initialOutboundTicketInformation,
        consumerInfo,
        transportationInfo,
        shippingInfo,
        status,
        materialGrid: {
          'truck-total': {
            yardGross: String(truckWeight?.gross || 0),
            yardTare: String(truckWeight?.tare || 0),
            axleWeights,
          },
        },
        truckWeight,
        freightCost,
        pullApartRows:
          status?.value === OUTBOUND_TICKET_STATUSES.INCOMPLETE?.id
            ? formattedPullApartScrapRows
            : savedPullApartScrapRows,
      })

      setCustomerId(outboundTicketData.customerId)
      setShippingInfo(shippingInfo)
      setHasReset(true)
      if (consumerInfo.salesOrder) {
        initConsumerInfo(consumerInfo)
      }
      const formattedNotes = formatNotes(notes)
      setLoadNotes(formattedNotes)
      setValue('notes', formattedNotes)
      setValue('docs', docs)
      setValue('transportationInfo.transportationType', transportationInfo.transportationType)
    } else if (!isUpdate) {
      setValue('freightCost.userSet', false)
      setValue('freightCost.userChargebackSet', false)
      setValue('transportationInfo.transportationType', TRANSPORTATION_TYPE_OPTIONS[1].id)
    }
  }

  const setupMaterials = async () => {
    if (outboundTicketData) {
      const { truckWeight, axleWeights } = transformOutboundTicket(outboundTicketData)

      const materialRows = []

      outboundTicketData?.packingLists?.forEach(packingList => {
        packingList.finishedGoods?.forEach(finishedGood => {
          addFinishedGoodToMaterialRows(finishedGood, materialRows, packingList)
        })
      })

      outboundTicketData?.finishedGoods?.forEach(finishedGood => {
        addFinishedGoodToMaterialRows(finishedGood, materialRows)
      })

      outboundTicketData?.materialWeights?.forEach(materialItem => {
        const grossImages = findOutboundTicketImages({
          images: outboundTicketData?.images,
          imageType: OUTBOUND_TICKET_IMAGE_TYPE.MATERIAL_GROSS,
          materialTypeId: materialItem?.materialTypeId,
          materialRowIndex: materialItem?.rowIndex,
        })

        const tareImages = findOutboundTicketImages({
          images: outboundTicketData?.images,
          imageType: OUTBOUND_TICKET_IMAGE_TYPE.MATERIAL_TARE,
          materialTypeId: materialItem?.materialTypeId,
          materialRowIndex: materialItem?.rowIndex,
        })

        const { commonName: materialName, code: materialCode } =
          cachedMaterialTypesData?.[materialItem?.materialTypeId] || {}

        materialRows.push({
          id: randomId(),
          code: {
            materialName: materialName || '',
            materialCode: materialCode || '',
            materialTypeId: materialItem?.materialTypeId,
            orderEntryId: materialItem?.orderEntryId,
          },
          um: defaultYardUm,
          materialTypeId: materialItem?.materialTypeId,
          orderEntryId: materialItem?.orderEntryId,
          yardGross: materialItem?.gross || 0,
          grossImages: grossImages || [],
          yardTare: materialItem?.tare || 0,
          tareImages: tareImages || [],
          yardNet: materialItem?.weight || 0,
          axleWeights: materialItem?.axleWeights,
        })
      })

      setInitialMaterialRows(materialRows)
      const totalInfoFields = calculateTotalInfoFields(materialRows)
      const { loadGross, loadTare, totalNet } = totalInfoFields

      const truckGrossImages = outboundTicketData?.images
        ?.filter(image => image.type === OUTBOUND_TICKET_IMAGE_TYPE.OUTBOUND_TICKET_GROSS)
        .map(image => ({ ...image, imageFilename: image.filename }))
      // previous map is required because of the way the images are stored in the DB
      // 'filename' should be replaced by 'imageFilename' for consistency
      const truckTareImages = outboundTicketData?.images
        ?.filter(image => image.type === OUTBOUND_TICKET_IMAGE_TYPE.OUTBOUND_TICKET_TARE)
        .map(image => ({ ...image, imageFilename: image.filename }))
      // previous map is required because of the way the images are stored in the DB
      // 'filename' should be replaced by 'imageFilename' for consistency

      setInitialTotalInformation([
        {
          id: 'load-total',
          code: 'Load Total',
          um: defaultYardUm,
          yardGross: loadGross,
          yardTare: loadTare,
          yardNet: totalNet,
        },
        {
          id: 'truck-total',
          code: 'Truck Total',
          um: defaultYardUm,
          yardGross: truckWeight?.gross,
          grossImages: truckGrossImages || [],
          yardTare: truckWeight?.tare,
          tareImages: truckTareImages || [],
          yardNet: truckWeight?.net,
          axleWeights,
        },
      ])

      calculateTotalInfo()
    } else if (!isUpdate) {
      setInitialTotalInformation([
        {
          id: 'load-total',
          code: 'Load Total',
          um: defaultYardUm,
          yardGross: 0,
          yardTare: 0,
          yardNet: 0,
        },
        {
          id: 'truck-total',
          code: 'Truck Total',
          um: defaultYardUm,
          yardGross: 0,
          yardTare: 0,
          yardNet: 0,
          axleWeights: enableAxleWeights
            ? [...Array(defaultNumberOfAxles).keys()].map((_, index) => ({
                ...DEFAULT_AXLE_DATA,
                axleNumber: index + 1,
              }))
            : [],
        },
      ])
    }
  }

  useEffect(() => {
    setupOutboundTicket()
  }, [outboundTicketData, activePullApartScrapRows])

  useEffect(() => {
    setupMaterials()
  }, [outboundTicketData, materialsApiRef, cachedMaterialTypesData])

  useEffect(() => {
    if (materialsApiRef?.current?.subscribeEvent) {
      materialsApiRef.current.subscribeEvent('rowsUpdate', () => {
        calculateTotalInfo()
      })
    }
  }, [materialsApiRef])

  const getFGList = () => {
    const fgList = []
    const fgIDList = []

    getFinishedGoodsList().forEach(item => {
      if (!fgIDList.includes(item.id)) {
        fgList.push(item)
        fgIDList.push(item.id)
      }
    })

    return fgList
  }

  const formatData = data => {
    if (!totalApiRef?.current?.getRow) return

    const fgList = getFGList()

    const { axleWeights, ...truckWeights } = getTruckWeights()

    const dataFormatted = {
      customerId: data.consumerInfo?.consumerName?.id,
      packingLists: getPackingLists(),
      carrierId: data.transportationInfo?.carrierLookup?.id || null,
      transportationInfo: {
        carrier: data.transportationInfo?.driverName || '',
        carrierNumber: data.transportationInfo?.truck || '',
        trailerNumber: data.transportationInfo?.trailer || '',
        carrierLookup: {
          id: data.transportationInfo?.carrierLookup?.id || null,
          displayName: data.transportationInfo?.carrierLookup?.displayName || '',
          customerCommonIdentifierString: data.transportationInfo?.carrierLookup?.customerCommonIdentifierString || '',
        },
        fob: data.transportationInfo?.fob || '',
        container: data.transportationInfo?.container || '',
        seal: data.transportationInfo?.seal || '',
        export: data.transportationInfo?.export || false,
        transportationType: data.transportationInfo?.transportationType || '',
      },
      shippingInfo: {
        locationId: data.shippingInfo?.locationId || null,
        name: data.shippingInfo?.name || '',
        address: data.shippingInfo?.shippingAddress || '',
        city: data.shippingInfo?.city || '',
        state: data.shippingInfo?.state || '',
        zip: data.shippingInfo?.zip || '',
        ...(enableCountryCodes ? { country: data.shippingInfo?.country } : {}),
      },
      finishedGoods: fgList,
      materialWeights: getMaterials(),
      images: getImages(),
      status: data.status.value,
      contractInfo: {
        salesOrder: data.consumerInfo?.salesOrder || '',
        customerPo: data.consumerInfo?.customerPo || '',
        invoiceWeights: data.consumerInfo?.invoiceWeights || '',
        bookingRef: data.consumerInfo?.bookingRef || '',
        truckWeight: truckWeights,
      },
      millScaleNumber: data.consumerInfo?.millScaleNumber || '',
      notes: data.notes,
      uploadcareGroupId: data.uploadcareGroupId,
      docs: data.docs,
      freightCost: {
        cost: data.freightCost?.cost || 0,
        chargeBack: data.freightCost?.chargeBack || 0,
        userSet: data.freightCost?.userSet || false,
        userChargebackSet: data.freightCost?.userChargebackSet || false,
        id: data.freightCost?.id || undefined,
      },
      effectiveDate: new Date(data.consumerInfo?.effectiveDate).toISOString(),
      axleWeights,
      ...(enablePullApartFeatures ? { pullApartRows: data.pullApartRows } : {}),
    }

    return dataFormatted
  }

  const onSaveNotes = notes => {
    setValue('notes', notes, { shouldDirty: true })
    setOpenNotes(false)
    setLoadNotes(notes)
  }

  const handleSuccess = (data, newOutboundTicket) => {
    reset(data)
    setHasEditedMaterials(false)
    trackEvent(cz[`${isUpdate ? 'UPDATE' : 'CREATE'}_OUTBOUND_TICKET`])
    salesOrderShouldDirty.current = false
    dispatch(clearConsumerInformation())
    dispatchSnackbar(dispatch, 'success', 'Changes Saved')
    if (!isUpdate) {
      navigate(`/app/sales/outbound-tickets/${newOutboundTicket.id}`)
    }
  }

  const handleError = error => {
    let errorMsg
    if (error?.data?.error === 'pap error') {
      errorMsg = 'An error occurred while updating the scrap rows. Please refresh and try again.'
    } else if (error?.data?.message) {
      errorMsg = `${error?.data?.error || 'Error'}: ${error.data.message}`
    } else {
      errorMsg =
        error?.data?.error ||
        error?.error ||
        error?.message ||
        `An error occurred ${isUpdate ? 'updating' : 'creating'} the ticket.`
    }
    trackEvent({
      eventName: cz[`${isUpdate ? 'UPDATE' : 'CREATE'}_OUTBOUND_TICKET`],
      errorMsg,
    })
    setErrorMessage(errorMsg)
  }

  const onSubmit = async data => {
    const materialRows = Array.from(materialsApiRef.current.getRowModels().values())
    const missingMaterialTypeId = materialRows.some(
      row => !row.code?.materialTypeId && (!!row.yardGross || !!row.yardTare),
    )

    if (missingMaterialTypeId) {
      dispatchSnackbar(dispatch, 'error', 'Some rows do not have a material selected')
      return
    }

    const outboundTicket = formatData(data)
    if (isUpdate) {
      await updateOutboundTicket({
        outboundTicketId,
        outboundTicketRequestDto: outboundTicket,
      })
        .unwrap()
        .then(() => handleSuccess(data))
        .catch(handleError)
    } else {
      await createOutboundTicket({ outboundTicketRequestDto: outboundTicket })
        .unwrap()
        .then(newOutboundTicket => handleSuccess(data, newOutboundTicket))
        .catch(handleError)
    }
  }

  const debouncedOnSubmit = useMemo(() => debounce(onSubmit, 500), [])

  const onClickVoid = async () => {
    setShowVoidModal(false)

    await voidOutboundTicket({
      outboundTicketId,
      invoiceId: outboundTicketData?.invoiceId,
    })
      .unwrap()
      .then(() => {
        navigate('/app/sales')
      })
      .catch(error => setErrorMessage(error?.data?.error || 'An error occurred voiding the ticket.'))
  }

  const onUpdateMaterial = useCallback(() => {
    setHasEditedMaterials(true)
    calculateTotalInfo()
  })

  const ticketIsPaidInvoicedLockedOrVoided = useCallback(
    () =>
      [
        OUTBOUND_TICKET_STATUSES.PAID.id,
        OUTBOUND_TICKET_STATUSES.VOID.id,
        OUTBOUND_TICKET_STATUSES.INVOICED.id,
      ].indexOf(outboundTicketData?.status) > -1 || isInLockedAccountingPeriodV1,
    [outboundTicketData?.status, isInLockedAccountingPeriodV1],
  )

  const ticketIsLockedOrVoided = useCallback(
    () => [OUTBOUND_TICKET_STATUSES.VOID.id].indexOf(outboundTicketData?.status) > -1 || isInLockedAccountingPeriodV1,
    [outboundTicketData?.status, isInLockedAccountingPeriodV1],
  )

  const ticketIsPaidOrLockedOrVoided = useCallback(
    () =>
      [OUTBOUND_TICKET_STATUSES.PAID.id, OUTBOUND_TICKET_STATUSES.VOID.id].indexOf(outboundTicketData?.status) > -1 ||
      isInLockedAccountingPeriodV1,
    [outboundTicketData?.status, isInLockedAccountingPeriodV1],
  )

  useEffect(() => {
    tabsContext.setContainerTabsVisibility(false)
    dispatch(clearSalesOrderFilters())

    const containerTitle = outboundTicketData ? `Outbound Ticket #${outboundTicketData?.tag}` : 'New Outbound Ticket'
    context.setContainerTitle(
      <Stack direction="row" alignItems="center" spacing={1}>
        {isInLockedAccountingPeriodV2 && (
          <Tooltip title={generateLockTooltip('outbound ticket')} placement="top">
            <LockIcon color="info" />
          </Tooltip>
        )}
        <Typography variant="h5">{containerTitle}</Typography>
      </Stack>,
    )

    const iconMapper = {
      INCOMPLETE: <IncompleteIcon />,
      PAID: <PaidIcon />,
      SHIPPED: (
        <LocalShippingIcon
          sx={{
            '&.MuiChip-icon': {
              color: theme.palette.primary.main,
            },
          }}
        />
      ),
    }
    if (formValues?.status?.value === 'INVOICED') {
      context.setContainerStatus(
        <InvoicedStatusChip
          tooltipText={
            supportVoidInvoice
              ? 'In order to edit this ticket, either remove this ticket from the invoice or void the corresponding invoice entirely.'
              : 'In order to edit this ticket, remove this ticket from the invoice.'
          }
          chipSx={{ fontWeight: 500, fontSize: '16px', padding: 2 }}
        />,
      )
    } else if (formValues?.status?.value === 'PAID') {
      context.setContainerStatus(
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip
            icon={<PaidIcon />}
            label={OUTBOUND_TICKET_STATUSES.PAID.name}
            sx={{ fontWeight: 500, fontSize: '16px', padding: 2 }}
          />
        </Stack>,
      )
    } else if (formValues?.status?.value === 'VOID') {
      context.setContainerStatus(
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip label={OUTBOUND_TICKET_STATUSES.VOID.name} sx={{ fontWeight: 500, fontSize: '16px', padding: 2 }} />
        </Stack>,
      )
    } else if (materialsContainFlats && outboundTicketData?.status === 'SHIPPED') {
      context.setContainerStatus(
        <Stack direction="row" alignItems="center" spacing={1}>
          <Chip label={OUTBOUND_TICKET_STATUSES.SHIPPED.name} sx={{ fontWeight: 500, fontSize: '16px', padding: 2 }} />
        </Stack>,
      )
    } else {
      context.setContainerStatus(
        <Tooltip title={isInLockedAccountingPeriodV1 ? ENTITY_IN_LOCK_PERIOD_MESSAGE : ''}>
          <div>
            <SelectChip
              control={control}
              items={statusArray}
              name="status.value"
              defaultValue={statusArray[0].id}
              disabled={ticketIsPaidInvoicedLockedOrVoided()}
              selectProps={{
                IconComponent: ArrowDropDownIcon,
                renderValue: value => (
                  <Chip
                    icon={iconMapper[value]}
                    label={OUTBOUND_TICKET_STATUSES[value]?.name}
                    sx={{
                      border: 'none',
                      fontWeight: 500,
                      fontSize: '16px',
                    }}
                  />
                ),
              }}
            />
          </div>
        </Tooltip>,
      )
    }

    const saveAction = {
      text: outboundTicketData ? 'Save Ticket' : 'Create Ticket',
      icon: outboundTicketData ? <SaveIcon /> : null,
      disabled: isLoading || (!isDirty && !hasEditedMaterials) || (materialsContainFlats && hasPullApartError),
      id: 'save-button',
      onClick: methods.handleSubmit(debouncedOnSubmit),
      type: ACTION_TYPE.TooltipButton,
      tooltipProps: {
        title:
          materialsContainFlats && hasPullApartError
            ? 'There was an error while fetching the available scrap rows. Please refresh and try again.'
            : '',
      },
    }
    const cancelAction = {
      text: 'Cancel',
      variant: 'text',
      disabled: isLoading,
      id: 'cancel-button',
      onClick: () => {
        navigate(`/app/sales/outbound-tickets/${outboundTicketId || ''}`)
      },
    }
    const extraAction = {
      text: '',
      variant: 'text',
      disabled: isLoading,
      id: 'more-actions-button',
      onClick: option => option.onClick(),
      type: ACTION_TYPE.SelectButton,
      endIcon: <MoreVertIcon />,
      options: getMoreActionsOptions(),
    }
    const printAction = {
      text: 'Print',
      variant: 'text',
      disabled: isLoading,
      id: 'outbound-ticket-print-select',
      ...(useMinimalObt
        ? {
            type: ACTION_TYPE.Button,
            icon: <PrintIcon />,
            onClick: () =>
              handleOpenPrintDialog(
                getDialogContents(outboundTicketData?.tag)?.find(option => option.id === 'billOfLading'),
              ),
          }
        : {
            type: ACTION_TYPE.SelectButton,
            startIcon: <PrintIcon />,
            options: getDialogContents(outboundTicketData?.tag),
            onClick: option => handlePrint(option),
          }),
    }

    const emailAction = {
      text: 'Email',
      variant: 'text',
      disabled: isLoading,
      id: 'outbound-ticket-email-select',
      onClick: () => setIsEmailModalOpen(true),
    }

    const invoiceAction = {
      icon: <AttachMoneyIcon />,
      text: `Invoice${outboundTicketData?.invoiceId ? ' Details' : ''}`,
      variant: outboundTicketData?.invoiceId ? 'text' : 'contained',
      disabled: isLoading,
      id: 'outbound-ticket-invoice-button',
      onClick: () => {
        if (outboundTicketData?.invoiceId) {
          navigate(`/app/sales/invoices/${outboundTicketData.invoiceId}`)
        } else {
          dispatch(setOutboundTicketId(outboundTicketData?.id))
          navigate('/app/sales/invoices/new')
        }
      },
    }

    const effectiveEnablePdfEmails = useMinimalObt ? false : enablePdfEmails

    if (!outboundTicketData) {
      context.setContainerActions([saveAction, cancelAction])
    } else if (outboundTicketData?.status === OUTBOUND_TICKET_STATUSES.VOID.id) {
      context.setContainerActions(...[effectiveEnablePdfEmails ? [emailAction, printAction] : [printAction]])
    } else if (outboundTicketData?.status === OUTBOUND_TICKET_STATUSES.INVOICED.id) {
      context.setContainerActions([
        saveAction,
        invoiceAction,
        ...(effectiveEnablePdfEmails ? [emailAction, printAction] : [printAction]),
        extraAction,
      ])
    } else if (isDirty || hasEditedMaterials) {
      context.setContainerActions([saveAction, cancelAction, extraAction])
    } else if (
      [OUTBOUND_TICKET_STATUSES.SHIPPED.id, OUTBOUND_TICKET_STATUSES.PAID.id].includes(outboundTicketData?.status) &&
      outboundTicketData?.contractInfo?.salesOrder
    ) {
      context.setContainerActions([
        invoiceAction,
        ...(effectiveEnablePdfEmails ? [emailAction, printAction] : [printAction]),
        extraAction,
      ])
    } else {
      context.setContainerActions([
        ...(effectiveEnablePdfEmails ? [emailAction, printAction] : [printAction]),
        extraAction,
      ])
    }
  }, [
    outboundTicketData,
    isDirty,
    hasEditedMaterials,
    isLoading,
    useMinimalObt,
    isInLockedAccountingPeriodV1,
    materialsContainFlats,
    hasPullApartError,
  ])

  useEffect(() => {
    dispatch(clearSubmittedFromDispatchBlade())
    if (initialRender.current) return

    const { salesOrder, customerPo, invoiceWeights } = consumerInfoState
    if (salesOrder !== undefined && salesOrder !== null) {
      setValue('consumerInfo.salesOrder', salesOrder, { shouldDirty: salesOrderShouldDirty.current })
      setValue('consumerInfo.customerPo', customerPo, { shouldDirty: salesOrderShouldDirty.current })
      setValue('consumerInfo.invoiceWeights', invoiceWeights, { shouldDirty: salesOrderShouldDirty.current })
    } else {
      setValue('consumerInfo.salesOrder', '')
      setValue('consumerInfo.customerPo', '')
      setValue('consumerInfo.invoiceWeights', '')
    }
  }, [consumerInfoState])

  useEffect(() => {
    if (consumerInfoState?.consumer?.id) {
      setValue('consumerInfo.consumerName', consumerInfoState.consumer)
      setCustomerId(consumerInfoState.consumer?.id)
    }
  }, [consumerInfoState?.consumer])

  const onUploadFiles = useCallback(uploadUUID => {
    setValue('uploadcareGroupId', uploadUUID)
    debouncedOnSubmit(getValues())
  }, [])

  const isUploadFilesDisabled = () => {
    if (isUpdate) return !isValid
    return true
  }

  const handleRemoveFiles = files => {
    setValue('docs', files)
    debouncedOnSubmit(getValues())
  }

  const handleUnlinkDispatchTicket = async () => {
    setIsUnlinking(true)

    await patchDispatchTicket({
      patchDispatchTicketBody: {
        outboundTicketId: null,
        hash: outboundTicketData?.linkedDispatchTicket?.hash,
      },
      dispatchTicketId: outboundTicketData?.linkedDispatchTicket?.id,
      outboundTicketId: outboundTicketData?.id,
      assetIds: [],
    })
      .unwrap()
      .then(() => {
        dispatchSnackbar(dispatch, 'success', 'Dispatch Ticket successfully unlinked.')
      })
      .catch(error => {
        let errorMessage = 'There was an error unlinking the Dispatch Ticket.'

        if (error?.data?.code === GS_ERROR_CODE.CONCURRENCY_VIOLATION) {
          errorMessage = `Dispatch ticket has been updated by another user. Please refresh your page and try again.`
        }

        dispatchSnackbar(dispatch, 'error', errorMessage)
      })
      .finally(() => {
        setShowUnlinkModal(false)
        setIsUnlinking(false)
      })
  }

  const handleChangeShippingCountry = newCountry => {
    setValue('shippingInfo.name', '', { shouldDirty: true })
    setValue('shippingInfo.country', newCountry, { shouldDirty: true })
    const countryFromState = getCountryFromState(getValues('shippingInfo.state'))

    if (['US', 'CA'].includes(newCountry) && newCountry !== countryFromState) {
      // Need to reset state since US and CA have state dropdowns
      // Otherwise out of range state val won't show but will still be set
      setValue('shippingInfo.state', '', { shouldDirty: true })
    }
  }

  const date = watch('consumerInfo.effectiveDate')?.toISOString()
  const userSet = watch('freightCost.userSet')
  const userChargebackSet = watch('freightCost.userChargebackSet')
  const chargeback = watch('freightCost.chargeBack')
  const shippingInfoLocationId = watch('shippingInfo.locationId')

  const { isLoadingFreightLanesData, priorityFreightCostInfo } = useCalculateFreightCost({
    location: shippingInfoLocationId
      ? customerLocations?.find(location => location.id === shippingInfoLocationId)
      : getValues('shippingInfo'),
    transportationType,
    carrierId: selectedCarrier?.id || '',
    effectiveDate: date,
    netWeight: loadInformation?.loadWeight?.net || 0,
    ticketType: 'OBT',
    contract: enableSalesOrderFreightCosts && consumerInfoState?.salesOrderId ? salesOrderData : null,
  })

  const disableFreightCost = freightBillIsPaid || ticketIsLockedOrVoided()
  const disableChargeback = ticketIsPaidOrLockedOrVoided()

  useEffect(() => {
    if (!freightBillIsPaid && isDirty) {
      if (userSet === false && !disableFreightCost) {
        setValue('freightCost.cost', priorityFreightCostInfo?.freightCost, { shouldDirty: true })
      }
      if (userChargebackSet === false && !disableChargeback) {
        const chargeBackValue = priorityFreightCostInfo?.freightChargeback ?? 0
        setValue('freightCost.chargeBack', chargeBackValue, { shouldDirty: true })
      }
    }
  }, [isDirty, freightBillIsPaid, priorityFreightCostInfo, disableFreightCost, disableChargeback])

  useEffect(() => {
    initialRender.current = false
  }, [])

  const packingListOrBoLDetailsList = useMemo(() => {
    const arr = useMinimalObt
      ? [
          {
            field: 'Carrier',
            value:
              formValues?.transportationInfo?.carrierLookup?.displayName ||
              formValues?.transportationInfo?.carrierLookup,
          },
          {
            field: 'Driver Name',
            value: formValues?.transportationInfo?.driverName || '--',
          },
          {
            field: 'Site Location',
            value: formValues?.consumerInfo?.customerPo || '--',
          },
          {
            field: 'Truck #',
            value: formValues?.transportationInfo?.truck || '--',
          },
          {
            field: 'Trailer #',
            value: formValues?.transportationInfo?.trailer || '--',
          },
          {
            field: 'Time In',
            value: timeFormatter(formValues?.consumerInfo?.effectiveDate, defaultYardTimezone),
            value2: dateFormatter(formValues?.consumerInfo?.effectiveDate, defaultYardTimezone),
          },
          {
            field: 'Time Out',
            value: outboundTicketData?.firstShipDate
              ? timeFormatter(outboundTicketData.firstShipDate, defaultYardTimezone)
              : '--',
            value2: outboundTicketData?.firstShipDate
              ? dateFormatter(outboundTicketData.firstShipDate, defaultYardTimezone)
              : '--',
          },
        ]
      : [
          {
            field: 'Effective Date',
            value: dateFormatter(formValues?.consumerInfo?.effectiveDate) || '--',
          },
          {
            field: 'Carrier',
            value:
              formValues?.transportationInfo?.carrierLookup?.displayName ||
              formValues?.transportationInfo?.carrierLookup,
          },
          {
            field: 'Driver Name',
            value: formValues?.transportationInfo?.driverName || '--',
          },
          {
            field: 'Sales Order #',
            value: formValues?.consumerInfo?.salesOrder || '--',
          },
          {
            field: 'Customer PO #',
            value: formValues?.consumerInfo?.customerPo || '--',
          },
          {
            field: 'Booking Ref #',
            value: formValues?.consumerInfo?.bookingRef || '--',
          },
          {
            field: 'FOB',
            value: formValues?.transportationInfo?.fob || '--',
          },
          {
            field: 'Truck #',
            value: formValues?.transportationInfo?.truck || '--',
          },
          {
            field: 'Trailer #',
            value: formValues?.transportationInfo?.trailer || '--',
          },
          {
            field: 'Container #',
            value: formValues?.transportationInfo?.container || '--',
          },
          {
            field: 'Seal #',
            value: formValues?.transportationInfo?.seal || '--',
          },
        ]
    return arr.filter(item => activeDialog?.action === 'packingList' || item.field !== 'Booking Ref #')
  }, [formValues, useMinimalObt, activeDialog])

  const bolLooseMaterials = useMemo(() => {
    const looseMaterials = initialMaterialRows.filter(row => !Object.prototype.hasOwnProperty.call(row, 'fgId'))

    if (salesOrderEntriesObject) {
      const overrideMaterials = looseMaterials.map(material => {
        const { overrideName } = salesOrderEntriesObject?.find(entry => entry.id === material.orderEntryId) || {}
        return {
          ...material,
          code: {
            ...material.code,
            materialName: overrideName || material.code.materialName || '',
          },
        }
      })
      return overrideMaterials
    }

    return looseMaterials
  }, [initialMaterialRows, salesOrderEntriesObject, outboundTicketData, activeDialog])

  const notesToPrint = useMemo(() => {
    if (outboundTicketData) {
      const shouldIncludeSalesOrderNotes =
        salesOrderData?.notes &&
        salesOrderData?.showNotesOnBOL &&
        showNotesFromSOOnBOL &&
        activeDialog?.action === 'billOfLading'

      const filteredOutboundTicketNotes = outboundTicketData?.notes?.filter(note => !note.isInternal) || []

      return shouldIncludeSalesOrderNotes
        ? [{ name: salesOrderData.notes }, ...filteredOutboundTicketNotes]
        : filteredOutboundTicketNotes
    }
    return []
  }, [outboundTicketData, salesOrderData, activeDialog, showNotesFromSOOnBOL])

  const bolFinishedGoods = useMemo(() => {
    if (salesOrderEntriesObject) {
      const overrideMaterials = getFinishedGoodsListFullData().map(finishedGood => {
        const { overrideName } = salesOrderEntriesObject?.find(entry => entry.id === finishedGood.orderEntryId) || ''

        return {
          ...finishedGood,
          materialName: overrideName || finishedGood.code.materialName,
        }
      })
      return overrideMaterials
    }
    return []
  }, [initialMaterialRows, salesOrderEntriesObject, outboundTicketData, activeDialog, materialsApiRef.current])

  const filteredPriceSheetData = useMemo(
    () => (salesOrderId && salesOrderId === salesOrderData?.id ? { Items: [salesOrderData?.priceSheet] } : null),
    [outboundTicketData, salesOrderData, salesOrderId],
  )

  const formSalesOrderNumber = watch('consumerInfo.salesOrder')

  useEffect(() => {
    if (!isDirty) return // skip on initial page load
    if (!materialsApiRef?.current?.getRowModels) return // skip if getRowModels function doesn't exist
    if (formSalesOrderNumber && Number(formSalesOrderNumber) !== salesOrderData?.salesOrderTicketId) return // skip if still fetching new salesOrderData

    const currentRows = Array.from(materialsApiRef.current.getRowModels().values())
    const updatedRows = currentRows.map(row => {
      // use the first entry from the new sales order that matches the material's materialTypeId
      const salesOrderEntry = formSalesOrderNumber
        ? salesOrderData?.priceSheet?.entriesObj?.find(entry => entry.materialTypeId === row.code?.materialTypeId)
        : null
      return {
        ...row,
        code: {
          ...row.code,
          orderEntryId: salesOrderEntry?.id || null,
        },
        orderEntryId: salesOrderEntry?.id || null,
      }
    })
    materialsApiRef.current.updateRows(updatedRows)
  }, [materialsApiRef, formSalesOrderNumber, salesOrderData])

  return (
    <>
      <AlertSnackbar
        message={errorMessage}
        isOpen={!!errorMessage}
        severity="error"
        handleClose={() => setErrorMessage('')}
      />

      {activeDialog && (
        <PrintDialog
          title={activeDialog.dialogTitle}
          open={!!activeDialog}
          onClose={handleClosePrintDialog}
          showLogoAddress
        >
          <PrintablePackingListOrBoL
            details={packingListOrBoLDetailsList}
            finishedGoods={bolFinishedGoods}
            hasSalesSection={false}
            notes={notesToPrint}
            customer={{ name: outboundTicketData?.customerDisplayName }}
            type={activeDialog.action}
            shippingInfo={getShippingInfo()}
            showShippingDetails={false}
            tag={outboundTicketData?.tag}
            millScaleNumber={outboundTicketData?.millScaleNumber}
            millDeliveryNumber={formValues?.consumerInfo?.millDeliveryNumber}
            bookingRef={outboundTicketData?.contractInfo?.bookingRef}
            looseMaterials={bolLooseMaterials}
            freightDestinationCustomerName={freightDestinationCustomerName}
            overall={getTruckWeights()}
          />
        </PrintDialog>
      )}

      <AlertModal
        open={showVoidModal}
        modalType="void"
        title="Void This Ticket"
        question="Are you sure you want to void this ticket?"
        description={`Ticket #${outboundTicketData?.tag} will be voided and all data
          on the ticket will be lost.`}
        confirmLabel="YES, VOID TICKET"
        cancelLabel="GO BACK"
        onClose={() => {
          setShowVoidModal(false)
        }}
        onSubmit={onClickVoid}
      />

      {showUnlinkModal && (
        <AlertModal
          open={showUnlinkModal}
          modalType="payment"
          title="Unlink Dispatch Ticket"
          question={`Are you sure you want to unlink Dispatch Ticket #${outboundTicketData?.linkedDispatchTicket?.externalId}?`}
          confirmLabel="UNLINK DISPATCH TICKET"
          cancelLabel="CANCEL"
          description=""
          onClose={() => {
            setShowUnlinkModal(false)
          }}
          onSubmit={handleUnlinkDispatchTicket}
          showIsLoading={isUnlinking}
        />
      )}

      <EmailModal
        open={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        recipients={customerData?.contacts?.map(contact => contact.email).filter(Boolean)}
        pdfTypeOptions={[PDF_TYPE.OBT_BILL_OF_LADING, PDF_TYPE.OBT_PACKING_LIST]}
        defaultPdfType={PDF_TYPE.OBT_BILL_OF_LADING}
        entityId={outboundTicketData?.id}
        allowMultiple={emailBOLsAndPLs}
      />
      {materialsContainFlats && hasPullApartError && !isLoading && (
        <Alert severity="error">
          There was an error while fetching the available scrap rows. Please refresh and try again.
        </Alert>
      )}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(debouncedOnSubmit)}>
          <RouterPrompt
            title="Leave This Page"
            question="Are you sure you want to leave this page?"
            description="You have unsaved data on this page and all unsaved data on the ticket will be lost."
            confirmLabel="YES, LEAVE PAGE"
            cancelLabel="GO BACK"
            when={!isClearingReduxFormState && (isDirty || hasEditedMaterials) && !submittedFromDispatchBlade}
            onSubmitAction={() => {
              if (outboundTicketData) {
                dispatch(clearConsumerInformation())
                dispatch(clearSalesOrderId())
                dispatch(clearSalesOrderFilters())
              }
            }}
          />

          <FreightCostBlade
            open={openFreightCostBlade}
            setOpen={setOpenFreightCostBlade}
            getValues={getValues}
            setValue={setValue}
            ticketStatus={outboundTicketData?.status}
            invoiceStatus={outboundTicketData?.invoice?.status}
            netWeight={loadInformation?.loadWeight?.net || 0}
            location={
              shippingInfoLocationId
                ? customerLocations?.find(location => location.id === shippingInfoLocationId)
                : getValues('shippingInfo')
            }
            type="OBT"
            contract={enableSalesOrderFreightCosts && consumerInfoState?.salesOrderId ? salesOrderData : null}
            freightBillData={outboundTicketData?.freightCost?.freightBill || null}
            isInLockedAccountingPeriodV1={isInLockedAccountingPeriodV1}
          />

          <Stack minHeight="calc(100vh - 235px)">
            <Stack direction={{ xs: 'column', md: 'row' }} p={3} spacing={3}>
              <Box width={{ xs: '100%', md: '50%' }} data-testid="consumer-contract-info">
                <Stack direction="row" justifyContent="space-between">
                  <Typography mt={1} variant="h6" gutterBottom color="textSecondary">
                    Consumer / Contract Info
                  </Typography>
                  {!useMinimalObt && showTransfer ? (
                    <Checkbox
                      name="transfer"
                      label="Mark as transfer"
                      size="small"
                      control={control}
                      checked={isTransfer}
                      onChange={handleClickTransfer}
                      disabled={ticketIsPaidInvoicedLockedOrVoided()}
                    />
                  ) : null}
                </Stack>
                <Stack
                  padding={2}
                  spacing={2}
                  sx={{
                    backgroundColor: 'background.section',
                    borderRadius: '8px',
                  }}
                >
                  {useMinimalObt ? null : (
                    <Stack direction="row" spacing={2}>
                      <Input
                        name="consumerInfo.salesOrder"
                        label="Sales Order"
                        data-test="consumer-info-sales-order"
                        variant="outlined"
                        size="small"
                        defaultValue=""
                        control={control}
                        error={!!errors.consumerInfo?.salesOrder}
                        helperText={errors.consumerInfo?.salesOrder?.message}
                        disabled
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                disabled={ticketIsPaidInvoicedLockedOrVoided()}
                                onClick={() => {
                                  salesOrderShouldDirty.current = true
                                  store.dispatch(
                                    showFormModal({
                                      formModalName: FORM_MODALS.LINK_SALE_ORDER,
                                    }),
                                  )
                                }}
                              >
                                <ManageSearchIcon color={ticketIsPaidInvoicedLockedOrVoided() ? 'unset' : 'primary'} />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Input
                        name="consumerInfo.customerPo"
                        label="Customer Order #"
                        data-test="consumer-info-customer-po"
                        variant="outlined"
                        size="small"
                        defaultValue=""
                        control={control}
                        error={!!errors.consumerInfo?.customerPo}
                        helperText={errors.consumerInfo?.customerPo?.message}
                        disabled={hasSalesOrderLinked() || ticketIsPaidInvoicedLockedOrVoided()}
                      />
                      <Input
                        name="consumerInfo.invoiceWeights"
                        label="Invoice Weights"
                        data-test="consumer-info-invoice-weights"
                        variant="outlined"
                        size="small"
                        defaultValue=""
                        control={control}
                        error={!!errors.consumerInfo?.invoiceWeights}
                        helperText={errors.consumerInfo?.invoiceWeights?.message}
                        disabled={hasSalesOrderLinked() || ticketIsPaidInvoicedLockedOrVoided()}
                      />
                    </Stack>
                  )}
                  <Stack direction="row" spacing={2}>
                    <CustomerAutocomplete
                      name="consumerInfo.consumerName"
                      label="Consumer Name"
                      data-test="consumer-info-consumer-name"
                      variant="outlined"
                      size="small"
                      control={control}
                      freeSolo={false}
                      error={!!errors.consumerInfo?.consumerName}
                      disabled={hasSalesOrderLinked() || ticketIsPaidInvoicedLockedOrVoided()}
                      onChange={handleCustomerNameSelected}
                      baseFilter={customersFilter}
                      autocompleteProps={{
                        renderOption: (props, option) => (
                          <li {...props} key={option.id}>
                            {option.displayName}
                          </li>
                        ),
                        isOptionEqualToValue: (option, value) => option?.id === value?.id,
                      }}
                    />
                    {useMinimalObt ? null : (
                      <Input
                        name="consumerInfo.bookingRef"
                        label="Booking Ref #"
                        data-test="consumer-info-booking-ref"
                        variant="outlined"
                        size="small"
                        defaultValue=""
                        control={control}
                        error={!!errors.consumerInfo?.bookingRef}
                        helperText={errors.consumerInfo?.bookingRef?.message}
                        disabled={ticketIsPaidInvoicedLockedOrVoided()}
                      />
                    )}
                    <DatePickerV2
                      name="consumerInfo.effectiveDate"
                      label="Effective Date"
                      data-test="consumer-info-effective-date"
                      variant="outlined"
                      size="small"
                      error={!!errors.consumerInfo?.effectiveDate}
                      helperText={errors.consumerInfo?.effectiveDate?.message}
                      control={control}
                      defaultDate={new Date()}
                      InputLabelProps={{ shrink: true }}
                      datePickerProps={{
                        disabled: ticketIsPaidInvoicedLockedOrVoided(),
                        minDate: lockAccountingPeriod.isLockedForAccounting
                          ? addToDate(new Date(lockAccountingPeriod.lockAccountingDate))
                          : undefined,
                      }}
                    />
                  </Stack>
                  {useMinimalObt ? (
                    <Stack direction="row" spacing={2}>
                      <Input
                        name="consumerInfo.customerPo"
                        label="Site Location"
                        data-test="consumer-info-customer-po"
                        variant="outlined"
                        size="small"
                        defaultValue=""
                        control={control}
                        error={!!errors.consumerInfo?.customerPo}
                        helperText={errors.consumerInfo?.customerPo?.message}
                        disabled={hasSalesOrderLinked()}
                      />
                    </Stack>
                  ) : null}
                  <Stack direction="row" spacing={2}>
                    {useMinimalObt ? null : (
                      <Input
                        name="consumerInfo.millDeliveryNumber"
                        label="Mill Delivery #"
                        data-test="mill-delivery-number"
                        variant="outlined"
                        size="small"
                        defaultValue=""
                        control={control}
                        error={!!errors.consumerInfo?.millDeliveryNumber}
                        helperText={errors.consumerInfo?.millDeliveryNumber?.message}
                        disabled
                      />
                    )}
                    <Input
                      name="consumerInfo.millScaleNumber"
                      label={useMinimalObt ? 'WeighMaster' : 'Mill Scale Ticket #'}
                      data-test="mill-scale-ticket-number"
                      variant="outlined"
                      size="small"
                      defaultValue=""
                      control={control}
                      error={!!errors.consumerInfo?.millScaleNumber}
                      helperText={errors.consumerInfo?.millScaleNumber?.message}
                      disabled={ticketIsPaidInvoicedLockedOrVoided()}
                    />
                  </Stack>
                </Stack>
              </Box>
              <Box width={{ xs: '100%', md: '50%' }}>
                <Typography mt={1} variant="h6" gutterBottom color="textSecondary">
                  Transportation Info
                </Typography>
                <Stack
                  padding={2}
                  spacing={2}
                  sx={{
                    backgroundColor: 'background.section',
                    borderRadius: '8px',
                  }}
                >
                  <Stack direction="row" spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <CustomerAutocomplete
                        name="transportationInfo.carrierLookup"
                        label="Carrier"
                        data-test="transportation-info-carrier-lookup"
                        variant="outlined"
                        size="small"
                        defaultValue={null}
                        control={control}
                        error={!!errors.transportationInfo?.carrierLookup}
                        helperText={errors.transportationInfo?.carrierLookup?.message}
                        freeSolo={false}
                        baseFilter={CUSTOMERS_FILTERS.CARRIER}
                        autocompleteProps={{
                          renderOption: (props, option) => (
                            <li {...props} key={option.id}>
                              {option.displayName}
                            </li>
                          ),
                          isOptionEqualToValue: (option, value) => option?.id === value?.id,
                        }}
                        disabled={ticketIsLockedOrVoided() || freightBillIsPaid}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Input
                        name="transportationInfo.driverName"
                        label="Driver Name"
                        data-test="transportation-info-driver-name"
                        variant="outlined"
                        size="small"
                        defaultValue=""
                        control={control}
                        error={!!errors.transportationInfo?.driverName}
                        helperText={errors.transportationInfo?.driverName?.message}
                        disabled={ticketIsLockedOrVoided() || freightBillIsPaid}
                      />
                    </Box>
                    {appRouteLoadBoard && (
                      <Box sx={{ flex: 1, minWidth: 220 }}>
                        <DispatchTicketFormAreaObt
                          outboundTicketData={outboundTicketData}
                          formattedObtData={formatData(formValues)}
                          dispatch={dispatch}
                          setShowUnlinkModal={setShowUnlinkModal}
                          cachedMaterialTypesData={cachedMaterialTypesData}
                          disabled={ticketIsPaidOrLockedOrVoided()}
                          formData={getValues()}
                          salesOrderData={salesOrderData}
                        />
                      </Box>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    {!useMinimalObt && (
                      <Input
                        name="transportationInfo.fob"
                        label="FOB/INCO"
                        data-test="transportation-info-fob"
                        variant="outlined"
                        size="small"
                        defaultValue=""
                        control={control}
                        error={!!errors.transportationInfo?.fob}
                        helperText={errors.transportationInfo?.fob?.message}
                        disabled={ticketIsLockedOrVoided() || freightBillIsPaid}
                      />
                    )}
                    <Input
                      name="transportationInfo.truck"
                      label="Truck #"
                      data-test="transportation-info-truck"
                      variant="outlined"
                      size="small"
                      defaultValue=""
                      control={control}
                      error={!!errors.transportationInfo?.truck}
                      helperText={errors.transportationInfo?.truck?.message}
                      disabled={ticketIsLockedOrVoided() || freightBillIsPaid}
                    />
                    <Input
                      name="transportationInfo.trailer"
                      label="Trailer #"
                      data-test="transportation-info-trailer"
                      variant="outlined"
                      size="small"
                      defaultValue=""
                      control={control}
                      error={!!errors.transportationInfo?.trailer}
                      helperText={errors.transportationInfo?.trailer?.message}
                      disabled={ticketIsLockedOrVoided() || freightBillIsPaid}
                    />
                  </Stack>

                  <Stack direction="row" spacing={2}>
                    {!useMinimalObt && (
                      <Box sx={{ flex: 1 }}>
                        <Input
                          name="transportationInfo.container"
                          label="Container #"
                          data-test="transportation-info-container"
                          variant="outlined"
                          size="small"
                          defaultValue=""
                          control={control}
                          error={!!errors.transportationInfo?.container}
                          helperText={errors.transportationInfo?.container?.message}
                          disabled={ticketIsLockedOrVoided() || freightBillIsPaid}
                        />
                      </Box>
                    )}
                    {!useMinimalObt && (
                      <>
                        <Box sx={{ flex: 1 }}>
                          <Input
                            name="transportationInfo.seal"
                            label="Seal #"
                            data-test="transportation-info-seal"
                            variant="outlined"
                            size="small"
                            defaultValue=""
                            control={control}
                            error={!!errors.transportationInfo?.seal}
                            helperText={errors.transportationInfo?.seal?.message}
                            disabled={ticketIsLockedOrVoided() || freightBillIsPaid}
                          />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Checkbox
                            name="transportationInfo.export"
                            label="Export"
                            size="small"
                            control={control}
                            onChange={event => setValue('transportationInfo.export', event.target.checked)}
                            disabled={ticketIsLockedOrVoided() || freightBillIsPaid}
                          />
                        </Box>
                      </>
                    )}
                  </Stack>
                </Stack>
              </Box>
            </Stack>
            {useMinimalObt ? null : (
              <Box px={3}>
                <Typography variant="h6" gutterBottom color="textSecondary">
                  Shipping Info
                </Typography>
                <Stack
                  padding={2}
                  spacing={2}
                  direction={{ xs: 'column', md: 'row' }}
                  sx={{
                    backgroundColor: 'background.section',
                    borderRadius: '8px',
                  }}
                >
                  <Stack direction="row" spacing={2} width={{ xs: '100%', md: '50%' }}>
                    <Controller
                      name="shippingInfo.name"
                      render={() => (
                        <Autocomplete
                          label="Location Name"
                          data-test="shipping-info-name"
                          variant="outlined"
                          size="small"
                          control={control}
                          error={errors.shippingInfo?.name}
                          value={getValues('shippingInfo') || ''}
                          onChange={handleShippingInfoSelected}
                          onInputChange={handleAutoCompleteChange}
                          getOptionLabel={option => option?.name || ''}
                          fullWidth
                          options={customerData?.locations?.filter(location => locationIsNotEmpty(location)) || []}
                          isOptionEqualToValue={(option, value) => option?.name === value?.name}
                          renderInput={params => <TextField {...params} label="Location Name" />}
                          disabled={hasSalesOrderLinked() || ticketIsPaidInvoicedLockedOrVoided()}
                        />
                      )}
                    />
                    {enableCountryCodes && hasInternationalCustomers && !!customerLocations.length && (
                      <Select
                        name="shippingInfo.country"
                        label="Country"
                        data-test="shipping-info-country"
                        variant="outlined"
                        size="small"
                        defaultValue=""
                        control={control}
                        error={!!errors.shippingInfo?.country}
                        items={countries}
                        selectProps={{
                          onChange: e => {
                            handleChangeShippingCountry(e.target.value)
                          },
                        }}
                        disabled={hasSalesOrderLinked() || ticketIsPaidInvoicedLockedOrVoided()}
                      />
                    )}
                    <Input
                      name="shippingInfo.shippingAddress"
                      label="Shipping Address"
                      data-test="shipping-info-shipping-address"
                      variant="outlined"
                      size="small"
                      defaultValue=""
                      control={control}
                      error={!!errors.shippingInfo?.shippingAddress}
                      helperText={errors.shippingInfo?.shippingAddress?.message}
                      onChange={handleInputChange}
                      disabled={hasSalesOrderLinked() || ticketIsPaidInvoicedLockedOrVoided()}
                    />
                  </Stack>
                  <Stack direction="row" spacing={2} width={{ xs: '100%', md: '50%' }}>
                    <Input
                      name="shippingInfo.city"
                      label="City"
                      data-test="shipping-info-city"
                      variant="outlined"
                      size="small"
                      defaultValue=""
                      control={control}
                      error={!!errors.shippingInfo?.city}
                      helperText={errors.shippingInfo?.city?.message}
                      onChange={handleInputChange}
                      disabled={hasSalesOrderLinked() || ticketIsPaidInvoicedLockedOrVoided()}
                    />
                    <StateProvinceInput
                      key={'shippingInfo.state'}
                      name={'shippingInfo.state'}
                      defaultValue=""
                      control={control}
                      error={!!errors.shippingInfo?.state}
                      data-testid={'shipping-info-state'}
                      variant="outlined"
                      size="small"
                      disabled={hasSalesOrderLinked() || ticketIsPaidInvoicedLockedOrVoided()}
                      helperText={errors.shippingInfo?.state?.message}
                      enableCountryCodes={enableCountryCodes}
                      country={watch('shippingInfo.country') || getCountryFromState(getShippingInfo().state)}
                    />
                    <Input
                      name="shippingInfo.zip"
                      label="Zip"
                      data-test="shipping-info-zip"
                      variant="outlined"
                      size="small"
                      defaultValue=""
                      control={control}
                      error={!!errors.shippingInfo?.zip}
                      helperText={errors.shippingInfo?.zip?.message}
                      onChange={handleInputChange}
                      disabled={hasSalesOrderLinked() || ticketIsPaidInvoicedLockedOrVoided()}
                    />
                  </Stack>
                </Stack>
              </Box>
            )}
            {enablePullApartFeatures && materialsContainFlats && (
              <PullApartFormComponent
                isFetchingPullApartScrapRows={isFetchingPullApartScrapRows}
                isLoading={isLoading}
                isUpdate={isUpdate}
                ticketStatus={outboundTicketData?.status}
                outboundTicketData={outboundTicketData}
                activePullApartScrapRows={activePullApartScrapRows}
                disabled={ticketIsPaidInvoicedLockedOrVoided()}
                pullApartRemovedRowsMessage={pullApartRemovedRowsMessage}
              />
            )}
            {materialTypeIds.map(materialTypeId => (
              <MaterialTypesDataFetcher
                key={materialTypeId}
                materialTypeId={materialTypeId}
                setCachedMaterialTypesData={setCachedMaterialTypesData}
              />
            ))}
            <ActiveMaterialList
              priceSheetsData={filteredPriceSheetData}
              salesOrderData={salesOrderData}
              onUpdateMaterial={onUpdateMaterial}
              isUpdate={isUpdate}
              materials={initialMaterialRows}
              totalInformation={initialTotalInformation}
              apiRef={materialsApiRef}
              totalApiRef={totalApiRef}
              loading={isLoading}
              setFocus={setFocus}
              isTransfer={isTransfer}
              disabled={ticketIsPaidInvoicedLockedOrVoided()}
            />
          </Stack>
          <OutboundTicketFooter
            loadInformation={loadInformation}
            onClickNotes={() => setOpenNotes(true)}
            notes={loadNotes}
            files={outboundTicketData?.docs}
            onUploadFiles={onUploadFiles}
            onRemoveFiles={handleRemoveFiles}
            disabledUploadFiles={isUploadFilesDisabled()}
            setOpenFreightCostBlade={setOpenFreightCostBlade}
            isLoadingFreightLanesData={isLoadingFreightLanesData}
            viewOnly={ticketIsPaidInvoicedLockedOrVoided()}
            carrierPricePrepend={carrierPricePrepend}
          />
          <NotesModal
            notes={loadNotes}
            open={openNotes}
            disabled={ticketIsPaidInvoicedLockedOrVoided()}
            onSave={onSaveNotes}
            onClose={() => setOpenNotes(false)}
          />
        </form>
      </FormProvider>
    </>
  )
}
export default OutboundTicketContainer
